import React, { useMemo } from 'react';
import {
  GetProductsOfRoomsByFloorsFloor,
  GetProductsOfRoomsByFloorsProduct,
  GetProductsOfRoomsByFloorsRoom
} from "src/model/api/v2/data-contracts";
import { MainAndSubItemIndex, NestedAccordionView } from "src/components/NestedAccordionView/NestedAccordionView";
import { FloorIcon } from "src/icons/planner/subcategoryIcons/floorIcons/FloorIcon";
import { FloorEditIcon } from "src/icons/planner/subcategoryIcons/floorIcons/FloorEditIcon";
import { FloorDeleteIcon } from "src/icons/planner/subcategoryIcons/floorIcons/FloorDeleteIcon";
import { SubcategoryIcon } from "src/icons/planner/subcategoryIcons/SubcategoryIcon";
import { theme } from "src/theme/mui-theme";
import { SelectedDevice, SelectedRoom } from "src/api/planner-ui-slice";
import { isOutdoorRoom, isUnassignedRoom } from "src/pages/PlannerPage/FloorView/floorViewActions";
import { calcImageURL } from "src/utils/url";

export const floorAccordionDomId = (floorId: number) => `floor-accordion-${floorId}`;

interface FloorAccordionViewProps {
    floors: GetProductsOfRoomsByFloorsFloor[];
    selectedFloorId: number | null;
    selectedRoom: SelectedRoom;
    selectedDevice: SelectedDevice;
    onEditFloor: (floor: GetProductsOfRoomsByFloorsFloor) => void;
    onDeleteFloor: (floor: GetProductsOfRoomsByFloorsFloor) => void;
    onEditRoom: (room: GetProductsOfRoomsByFloorsRoom) => void;
    onDeleteRoom: (room: GetProductsOfRoomsByFloorsRoom) => void;
    onFloorExpand?: (floor: GetProductsOfRoomsByFloorsFloor, isExpanded: boolean) => void;
    onRoomExpand?: (room: GetProductsOfRoomsByFloorsRoom, isExpanded: boolean) => void;
    onProductClick?: (product: GetProductsOfRoomsByFloorsProduct) => void;
}

export function FloorAccordionView({
    floors,
    selectedFloorId,
    selectedRoom,
    selectedDevice,
    onEditFloor,
    onDeleteFloor,
    onEditRoom,
    onDeleteRoom,
    onFloorExpand,
    onRoomExpand,
    onProductClick,
}: FloorAccordionViewProps) {
    const selectedMainAndSubItem = useMemo<MainAndSubItemIndex | null>(() => {
        if (selectedFloorId == null) return null;
        const mainIndex = floors.findIndex((floor) => floor.floor_id === selectedFloorId);
        if (mainIndex === -1) return null;
        if (selectedRoom.id == null) return { mainIndex };
        const subIndex = floors[mainIndex].rooms.findIndex((room) => room.room_id === selectedRoom.id);
        return subIndex === -1 ? { mainIndex } : { mainIndex, subIndex };
    }, [selectedFloorId, selectedRoom, floors]);

    return (
        <NestedAccordionView
            data={floors}
            getMainLabel={(floor: GetProductsOfRoomsByFloorsFloor) => floor.floor_name}
            getSubList={(floor: GetProductsOfRoomsByFloorsFloor) => floor.rooms}
            getSubLabel={(room: GetProductsOfRoomsByFloorsRoom) => room.room_name}
            getItems={(room: GetProductsOfRoomsByFloorsRoom) => room.products}
            getItemKey={(product: GetProductsOfRoomsByFloorsProduct) => product.product_id}
            getItemLabel={(product: GetProductsOfRoomsByFloorsProduct) => product.name}
            getMainDomId={(floor: GetProductsOfRoomsByFloorsFloor) => floorAccordionDomId(floor.floor_id)}

            renderMainIcon={() => (
                <FloorIcon />
            )}
            renderMainActions={(floor) => (
                <>
                    <span onClick={(event) => {
                        event.stopPropagation();
                        onEditFloor(floor);
                    }}>
                        <FloorEditIcon />
                    </span>

                    <span onClick={(event) => {
                        event.stopPropagation();
                        onDeleteFloor(floor);
                    }}>
                        <FloorDeleteIcon />
                    </span>
                </>
            )}

            renderSubIcon={(room, isExpanded) => (
                <SubcategoryIcon
                    id={room.room_type}
                    sx={{ color: isExpanded ? theme.palette.primary.main : theme.palette.text.primary }}
                />
            )}
            renderSubActions={(room, isExpanded) => {
                // Unassigned: no edit/delete; Outdoors: edit only (matches legacy planner)
                if (isUnassignedRoom(room)) return null;
                return (
                    <>
                        <span onClick={(event) => {
                            event.stopPropagation();
                            onEditRoom(room);
                        }}>
                            <FloorEditIcon sx={{ color: isExpanded ? theme.palette.primary.main : theme.palette.text.primary }} />
                        </span>

                        {!isOutdoorRoom(room) && (
                            <span onClick={(event) => {
                                event.stopPropagation();
                                onDeleteRoom(room);
                            }}>
                                <FloorDeleteIcon sx={{ color: isExpanded ? theme.palette.primary.main : theme.palette.text.primary }} />
                            </span>
                        )}
                    </>
                );
            }}

            renderItemIcon={(product: any) => calcImageURL(product.icon_path ?? null)}
            onMainItemExpansionStateChange={onFloorExpand}
            onSubExpansionStateChange={(_, room, isExpanded) => onRoomExpand?.(room, isExpanded)}
            expandedMainAndSubItem={selectedMainAndSubItem}
            expansionTrigger={selectedRoom}
            selectedItemKey={selectedDevice.productId}
            onItemClick={onProductClick}
            singleMainExpanded
        />
    );
}